import { useEffect, useMemo, useRef, useState } from 'react';
import { FullscreenExitOutlined, FullscreenOutlined } from '@ant-design/icons';
import { javascript } from '@codemirror/lang-javascript';
import { defaultHighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { Annotation, EditorState, StateEffect } from '@codemirror/state';
import { EditorView, lineNumbers, ViewUpdate } from '@codemirror/view';
import { solarizedDark } from '@fsegurai/codemirror-theme-solarized-dark';
import { Button } from 'antd';

import { classnames } from 'lib/classnames';

import styles from './ScriptEditor.module.less';

const cx = classnames(styles);
interface ScriptEditor {
  value?: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  className?: string;
}

const defaultExtensions = [
  solarizedDark,
  javascript(),
  lineNumbers(),
  syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
];

const External = Annotation.define<boolean>();

const ScriptEditor: React.FC<ScriptEditor> = ({ value = '', onChange = () => {}, readOnly, className }) => {
  const editorRef = useRef(null);
  const codeMirror = useRef<EditorView>(null);
  const [fullscreen, setFullScreen] = useState(false);
  const [initValue] = useState(value);

  const updateListener = useMemo(
    () =>
      EditorView.updateListener.of((vu: ViewUpdate) => {
        if (
          vu.docChanged &&
          typeof onChange === 'function' &&
          // Fix echoing of the remote changes:
          // If transaction is market as remote we don't have to call `onChange` handler again
          !vu.transactions.some(tr => tr.annotation(External))
        ) {
          const doc = vu.state.doc;
          const value = doc.toString();
          onChange(value);
        }
      }),
    [onChange],
  );

  useEffect(() => {
    const startState = EditorState.create({
      doc: initValue,
      extensions: [
        readOnly ? EditorView.editable.of(false) : EditorView.editable.of(true),
        updateListener,
        ...defaultExtensions,
      ],
    });
    const codeMirrorInit = new EditorView({
      state: startState,
      parent: editorRef.current,
    });
    codeMirror.current = codeMirrorInit;
    // 只初始化一次
  }, []);

  useEffect(() => {
    if (codeMirror.current) {
      codeMirror.current.dispatch({
        effects: StateEffect.reconfigure.of([
          readOnly ? EditorView.editable.of(false) : EditorView.editable.of(true),
          updateListener,
          ...defaultExtensions,
        ]),
      });
    }
  }, [readOnly, updateListener]);

  useEffect(() => {
    if (value === undefined) {
      return;
    }
    const view = codeMirror.current;
    const currentValue = view ? view.state.doc.toString() : '';
    if (view && value !== currentValue) {
      view.dispatch({
        changes: { from: 0, to: currentValue.length, insert: value || '' },
        annotations: [External.of(true)],
      });
    }
  }, [value]);

  useEffect(() => {
    codeMirror.current?.focus();
  }, [fullscreen]);

  return (
    <div className={cx('script-editor-wrapper', { 'is-fullscreen': fullscreen }, className)}>
      <div className={cx('box')}>
        <div className={cx('script-editor')} ref={editorRef} />
      </div>
      <Button
        className={cx('fullscreen-btn')}
        size="small"
        icon={fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
        onClick={() => {
          setFullScreen(!fullscreen);
        }}
      />
    </div>
  );
};

export default ScriptEditor;
